import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Message, Field, Modal, Toolbar, Header } from 'react-frontier';
import { Dropdown, Icon } from 'semantic-ui-react';
import { useUser } from '../HooksPublic';
import API from '../APIPublic';
import Validator from '@cocoan/components/Validator';
import toast from 'react-hot-toast';
import styles from '../style/user_account.module.scss';
import { bindFormChange, formatPhone10 } from '../UtilPublic';
import { UserCreditsBalance, UserLevelsData, UpdatePasswordData, UpdateUserData } from '../ClassesPublic';
import { UserAccountOrders, UserAccountPoints, UserAccountBenefits, UserAccountWallet } from '../components';
import moment from 'moment';

var UserAccount=()=>{
   var { login, user, logout, token }=useUser();
   var [tab, setTab]=useState<'orders' | 'points' | 'benefits' | 'wallet'>('benefits');
   var [isEditModalOpen, setIsEditModalOpen] = useState(false);
   var [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
   var [errorPrompts, setErrorPrompts]=useState<string[]>(null);
   var [updatePass, setUpdatePass]=useState<UpdatePasswordData>({ currentPassword: '', newPassword: '', confirm: '' });
   var [updateUser, setUpdateUser]=useState<UpdateUserData>({ last_name: '', first_name: '', phone: '', birthday: null, gender_id: null });
   var [confirmModal, setConfirmModal]=useState<boolean>(false);

   var [creditsBalance, setCreditsBalance] = useState<UserCreditsBalance>(null);
   var [levelsData, setLevelsData] = useState<UserLevelsData>(null);
   var [levelsError, setLevelsError] = useState<string>(null);
   var [resendLoading, setResendLoading] = useState(false);

   var bdayMonths = [
      { text: 'Enero', value: 0 },
      { text: 'Febrero', value: 1 },
      { text: 'Marzo', value: 2 },
      { text: 'Abril', value: 3 },
      { text: 'Mayo', value: 4 },
      { text: 'Junio', value: 5 },
      { text: 'Julio', value: 6 },
      { text: 'Agosto', value: 7 },
      { text: 'Septiembre', value: 8 },
      { text: 'Octubre', value: 9 },
      { text: 'Noviembre', value: 10 },
      { text: 'Diciembre', value: 11 },
   ];
   var bdayCurrentYear = useMemo(()=>moment().year(), []);
   var bdayYears = useMemo(()=>Array.from({length: 100}, (_, i) => ({ text: (bdayCurrentYear - i).toString(), value: bdayCurrentYear - i })), [bdayCurrentYear]);

   var [bdayDay, setBdayDay] = useState<number | undefined>(undefined);
   var [bdayMonth, setBdayMonth] = useState<number | undefined>(undefined);
   var [bdayYear, setBdayYear] = useState<number | undefined>(undefined);

   var bdayDaysCount = 31;
   if (bdayMonth !== undefined && bdayYear !== undefined) {
      bdayDaysCount = moment().year(bdayYear).month(bdayMonth).daysInMonth();
   } else if (bdayMonth !== undefined) {
      bdayDaysCount = moment().year(2000).month(bdayMonth).daysInMonth();
   }
   var bdayDays = useMemo(() => Array.from({length: bdayDaysCount}, (_, i) => ({ text: (i + 1).toString(), value: i + 1 })), [bdayDaysCount]);

   useEffect(()=>{
      if (bdayDay !== undefined && bdayMonth !== undefined && bdayYear !== undefined) {
         var m = moment().startOf('day').year(bdayYear).month(bdayMonth).date(bdayDay);
         if (updateUser.birthday !== m.unix()) {
            setUpdateUser(prev => ({ ...prev, birthday: m.unix() }));
         }
      } else {
         if (updateUser.birthday !== null) {
            setUpdateUser(prev => ({ ...prev, birthday: null }));
         }
      }
   }, [bdayDay, bdayMonth, bdayYear]);

   useEffect(()=>{
      if(!user) return;
      setUpdateUser({
         first_name: user.first_name,
         last_name: user.last_name,
         phone: formatPhone10(user.phone),
         birthday: user.birthday,
         gender_id: user.gender_id
      })
      if(user.birthday){
         var m=moment.unix(user.birthday);
         setBdayDay(m.date());
         setBdayMonth(m.month());
         setBdayYear(m.year());
      }
      if(!user.birthday || !user.gender_id || !user.phone) setIsEditModalOpen(true);
   }, [user])

   useEffect(()=>{
      if(!login || !user || !token){
         window.location.href=API.getAuthUrl();
         return;
      }
      API.getUserCreditsBalance(token.access).then(res => {
         if (!res.error) setCreditsBalance(res.data);
      }).catch(() => {});
      API.getAllLevels(token.access).then(res => {
         if (!res.error) {
             setLevelsData(res.data);
             setLevelsError(null);
         } else {
             setLevelsError(res.message || 'No fue posible cargar tus beneficios');
         }
      }).catch(() => {
          setLevelsError('Hubo un error cargando tus beneficios');
      });
   }, [login, user, token]);

   if(!login || !user || !token) return null;

   var handleSubmitAccount=(setLoading?: (l: boolean)=>void)=>{
      var { valid, prompts }=Validator(updateUser, {
         first_name: [{ rule: 'length', params: [2, 50], prompt: 'El nombre es demasiado corto' }],
         last_name: [{ rule: 'length', params: [2, 50], prompt: 'El apellido es demasiado corto' }],
         phone: [{ rule: 'length', params: [10], prompt: 'El teléfono debe tener 10 dígitos' }],
         birthday: [{ rule: 'empty', prompt: 'La fecha de nacimiento es obligatoria' }],
         gender_id: [{ rule: 'number', prompt: 'El género es obligatorio' }],
      });
      if(!valid) return setErrorPrompts(prompts);
      if(!token) return setErrorPrompts(['No se encontró sesión activa. Vuelve a iniciar sesión.']);
      if(setLoading) setLoading(true);
      API.updateUser(token.access, updateUser.first_name, updateUser.last_name, updateUser.phone, updateUser.birthday, updateUser.gender_id)
         .then(res => {
            if(res.error) return setErrorPrompts([res.message || 'Error actualizando datos']);
            toast.success('Datos actualizados correctamente', { position: 'top-center' });
            setIsEditModalOpen(false);
            try{
               var first_name=(updateUser.first_name || '').trim();
               var last_name=(updateUser.last_name || '').trim();
               var phone=formatPhone10(updateUser.phone || '');
               var newUserData={
                  email: user.email,
                  superadmin: user.superadmin,
                  user_id: user.user_id,
                  change_password: !!user.change_password,
                  first_name,
                  last_name,
                  phone,
                  birthday: updateUser.birthday,
                  gender_id: updateUser.gender_id,
                  name: `${first_name} ${last_name}`.trim(),
               };
               localStorage.setItem('user_data', JSON.stringify(newUserData));
               window.dispatchEvent(new Event('user'));
            }catch(e){}
         }).catch(()=>{
            setErrorPrompts(['Hubo un error inesperado al actualizar los datos.']);
         }).finally(()=>{
            if(setLoading) setLoading(false);
         });
   };
   
   var handleSubmitPassword=()=>{
      var { valid, prompts }=Validator(updatePass, {
         currentPassword: [{ rule: 'length', params: [8, 128], prompt: 'La contraseña actual debe tener al menos 8 caracteres' }],
         newPassword: [{ rule: 'length', params: [8, 128], prompt: 'La nueva contraseña debe tener al menos 8 caracteres' }],
      });
      if(!valid) return setErrorPrompts(prompts);
      if(!token) return setErrorPrompts(['No se encontró sesión activa. Vuelve a iniciar sesión.']);
      if(updatePass.newPassword !== updatePass.confirm) return setErrorPrompts(['Las contraseñas no coinciden.']);
      setConfirmModal(true);
   };
   
   var confirmChangePassword=(setLoading: (l: boolean)=>void)=>{
      setLoading(true);
      API.changePassword(updatePass.currentPassword, updatePass.newPassword, token.access)
         .then(res=>{
            if(res.error) return setErrorPrompts([res.message || 'Error cambiando la contraseña']);
            toast.success('Contraseña actualizada con éxito', { position: 'top-center' });
            setConfirmModal(false);
            setTimeout(()=>logout(),0);
         }).catch(()=>{
            setErrorPrompts(['Hubo un error inesperado cambiando la contraseña.']);
         }).finally(()=>setLoading(false));
   };
   
   var handleResendConfirm=()=>{
      if(!token) return;
      setResendLoading(true);
      API.resendConfirmEmail(token.access)
         .then(res => {
            if(res.error) return toast.error(res.message || 'Error al reenviar el correo', { position: 'top-center' });
            toast.success('El correo de confirmación ha sido enviado.', { position: 'top-center' });
         }).catch(()=>{
            toast.error('Hubo un error inesperado al reenviar el correo.', { position: 'top-center' });
         }).finally(() => {
            setResendLoading(false);
         });
   };

   var handleUpdateUser=bindFormChange(updateUser, setUpdateUser);
   var handleUpdatePass=bindFormChange(updatePass, setUpdatePass);


   return (
      <div className={`co user-account ${styles.container}`}>
         <div className={styles.profileWrapper}>
            {!user.email_confirmed && (
               <div style={{ padding: '10px 0px' }}>
                  <Message type='warning' text='Tu cuenta aún no ha sido verificada. Revisa tu correo electrónico para confirmar tu cuenta.' centered >
                     <Button loading={resendLoading} disabled={resendLoading} iconName='mail-forward' size='small' color='black' text='Reenviar correo' onClick={handleResendConfirm} style={{ marginTop: 20 }} />
                  </Message>
               </div>
            )}
            <div className={styles.profileTopRow}>
               <div className={styles.profileCard}>
                  <div className={styles.avatar}>
                     <span className={styles.avatarEmoji}>🍓</span>
                  </div>
                  <div className={styles.profileContent}>
                     <div className={styles.profileNameRow}>
                        <div>
                           <div className={styles.profileName}>{user.first_name} {user.last_name}</div>
                           <div className={styles.profileEmail}>{user.email}</div>
                        </div>
                        <div className={styles.profileIconsRow}>
                           <Icon name="lock" size="large" className={styles.iconButton} onClick={() => setIsPasswordModalOpen(true)} />
                           <Icon name="edit outline" size="large" className={styles.iconButton} onClick={() => setIsEditModalOpen(true)} />
                        </div>
                     </div>
                     <div className={styles.profileStatsRow}>
                        {creditsBalance && (
                           <>
                              <div className={styles.profileStat}>
                                 <div className={styles.profileStatValue}>
                                    <Icon name="star" className={styles.starIcon} />
                                    {creditsBalance.available}
                                 </div>
                                 <div className={styles.profileStatLabel}>Puntos</div>
                              </div>
                              <div className={styles.profileStatDivider} />
                           </>
                        )}
                        <div className={styles.profileStat}>
                           <div className={styles.profileStatValue}>
                              {(() => {
                                 if (!levelsData || !levelsData.levels || levelsData.levels.length === 0) return 'Sin nivel';
                                 const pts = creditsBalance?.available ?? levelsData.total_points ?? 0;
                                 const sortedLevels = [...levelsData.levels].sort((a, b) => a.points - b.points);
                                 let currentLevelName = 'Sin nivel';
                                 for (let i = 0; i < sortedLevels.length; i++) {
                                    if (pts >= sortedLevels[i].points) {
                                       currentLevelName = sortedLevels[i].level_name;
                                    } else {
                                       break;
                                    }
                                 }
                                 return currentLevelName;
                              })()}
                           </div>
                           <div className={styles.profileStatLabel}>Nivel</div>
                        </div>
                        <div className={styles.profileStatDivider} />
                        <div className={styles.profileStat}>
                           <div className={styles.profileStatValue}>{user.phone || '—'}</div>
                           <div className={styles.profileStatLabel}>Celular</div>
                        </div>
                     </div>
                  </div>
               </div>

               {creditsBalance && (
                  <>
                     <div className={styles.pointsCard}>
                        <div className={styles.pointsCardColumn}>
                           <div className={styles.pointsCardLabel}>Obtenidos</div>
                           <div className={styles.pointsCardValue}>{creditsBalance.collected}</div>
                        </div>
                        <div className={styles.pointsCardColumnCenter}>
                           <div className={styles.pointsCardLabelBold}>Mis puntos</div>
                           <div className={styles.pointsCardValueHighlight}>{creditsBalance.available}</div>
                        </div>
                        <div className={styles.pointsCardColumn}>
                           <div className={styles.pointsCardLabel}>Canjeados</div>
                           <div className={styles.pointsCardValue}>{creditsBalance.used}</div>
                        </div>
                     </div>
                  </>
               )}
            </div>

            <div className={styles.tabsContainer}>
               <Toolbar stretch divided>
                  <Toolbar.Item 
                     text="Mi tarjeta" 
                     active={tab === 'wallet'} 
                     onClick={()=>{ setTab('wallet'); setErrorPrompts(null) }} 
                  />
                  <Toolbar.Item 
                     text="Mis beneficios" 
                     active={tab === 'benefits'} 
                     onClick={()=>{ setTab('benefits'); setErrorPrompts(null) }} 
                  />
                  <Toolbar.Item 
                     text="Mis puntos" 
                     active={tab === 'points'} 
                     onClick={()=>{ setTab('points'); setErrorPrompts(null) }} 
                  />
                  <Toolbar.Item 
                     text="Mis pedidos" 
                     active={tab === 'orders'} 
                     onClick={()=>{ setTab('orders'); setErrorPrompts(null) }} 
                  />
               </Toolbar>
            </div>

            <div className={styles.contentSection}>
               <div className={styles.tabContentWrapper}>
                  {tab==='wallet' && <UserAccountWallet creditsBalance={creditsBalance} levelsData={levelsData} levelsError={levelsError} />}
                  {tab==='orders' && <UserAccountOrders />}
                  {tab==='points' && <UserAccountPoints />}
                  {tab==='benefits' && <UserAccountBenefits user={user} creditsBalance={creditsBalance} levelsData={levelsData} levelsError={levelsError} />}
               </div>
            </div>
         </div>

         <Modal open={isEditModalOpen} size="tiny" onClose={() => { setIsEditModalOpen(false); setErrorPrompts(null); }}>
            <Modal.Header>Editar perfil</Modal.Header>
            <Modal.Content>
               {(!user.birthday || !user.phone || !user.gender_id) && <Message text='Por favor actualiza tu información para seguir disfrutando de los beneficios.' type='warning' style={{ marginBottom: 10 }} />}
               <Input label="Nombre (s)" value={updateUser.first_name} onChange={handleUpdateUser('first_name')} />
               <Input label="Apellido" value={updateUser.last_name} onChange={handleUpdateUser('last_name')} />
               <Input maxLength={10} label="Celular" placeholder="10 dígitos" value={updateUser.phone} onChange={handleUpdateUser('phone')} />
               <Field label="Fecha de nacimiento" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                     <Dropdown fluid selection placeholder='Día' value={bdayDay} onChange={(_e, data) => setBdayDay(data.value as number)} options={bdayDays} style={{ minWidth: 70 }} />
                     <Dropdown fluid selection placeholder='Mes' value={bdayMonth} onChange={(_e, data) => setBdayMonth(data.value as number)} options={bdayMonths} style={{ minWidth: 100 }} />
                     <Dropdown fluid selection placeholder='Año' value={bdayYear} onChange={(_e, data) => setBdayYear(data.value as number)} options={bdayYears} style={{ minWidth: 80 }} />
                  </div>
               </Field>
               <Field label="Género">
                  <Dropdown
                     placeholder="Selecciona"
                     fluid
                     selection
                     style={{ height: 40, minHeight: 40, padding: '0 12px', borderRadius: 8, display: 'flex', alignItems: 'center' }}
                     value={updateUser.gender_id}
                     onChange={(_e, data) => handleUpdateUser('gender_id')(data.value as number)}
                     options={[
                        { text: 'Masculino', value: 1 },
                        { text: 'Femenino', value: 2 },
                        { text: 'Otro', value: 3 },
                     ]}
                  />
               </Field>
               {errorPrompts && errorPrompts.length > 0 && (
                  <Message type="error" list={errorPrompts} style={{ marginTop: 16 }} />
               )}
            </Modal.Content>
            <Modal.Actions>
               <Button text="Cancelar" basic onClick={() => { setIsEditModalOpen(false); setErrorPrompts(null); }} />
               <Button text="Guardar" color="black" onClick={() => handleSubmitAccount()} />
            </Modal.Actions>
         </Modal>

         <Modal open={isPasswordModalOpen} size="mini" onClose={() => { setIsPasswordModalOpen(false); setErrorPrompts(null); }}>
            <Modal.Header>Cambiar contraseña</Modal.Header>
            <Modal.Content>
               <Input
                  inputType="password"
                  label="Contraseña actual"
                  placeholder="Contraseña actual"
                  value={updatePass.currentPassword}
                  onChange={handleUpdatePass('currentPassword')}
               />
               <Input
                  inputType="password"
                  label="Nueva contraseña"
                  value={updatePass.newPassword}
                  placeholder="Nueva contraseña"
                  onChange={handleUpdatePass('newPassword')}
               />
               <Input
                  inputType="password"
                  value={updatePass.confirm}
                  label="Confirmar contraseña"
                  onChange={handleUpdatePass('confirm')}
                  placeholder="Confirmar contraseña"
               />
               {errorPrompts && errorPrompts.length > 0 && (
                  <Message type="error" list={errorPrompts} style={{ marginTop: 16 }} />
               )}
            </Modal.Content>
            <Modal.Actions>
               <Button text="Cancelar" basic onClick={() => { setIsPasswordModalOpen(false); setErrorPrompts(null); }} />
               <Button text="Guardar contraseña" color="black" onClick={() => handleSubmitPassword()} />
            </Modal.Actions>
         </Modal>

         <Modal open={confirmModal} size="mini" onClose={()=>setConfirmModal(false)}>
            <Modal.Header>Confirmación</Modal.Header>
            <Modal.Content>
               <Header text="¿Cambiar contraseña y cerrar sesión?" size="small" />
               <Message type="error" list={errorPrompts} />
            </Modal.Content>
            <Modal.Actions>
               <Button text="Cancelar" basic onClick={()=>setConfirmModal(false)} />
               <Button text="Confirmar" color="black" onClick={confirmChangePassword} />
            </Modal.Actions>
         </Modal>

      </div>
   );
};

export default UserAccount;
